/**
 * Referral bootstrap — runs at app launch to capture any referral code
 * present in the launch URL (web or native deep link) and stash it for
 * the useReferral hook to claim post-auth.
 *
 * Hooks called once at module import:
 *   - `captureFromCurrentUrl()`  — reads window.location.href once.
 *   - `attachNativeDeepLinkHook()` — wires Capacitor App.addListener
 *     so referral URLs that arrive while the app is already running
 *     (e.g. via Universal Link / Custom URL Scheme) also get captured.
 *
 * Idempotent against repeat calls — multiple captures of the same code
 * overwrite the localStorage cell with the same value. A newer code DOES
 * overwrite an older one (the user clearly clicked a different friend's
 * link), which matches user expectation.
 */

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { extractCodeFromUrl, rememberPendingCode } from './referralManager';

let urlCaptured = false;
let listenerAttached = false;

export function captureFromCurrentUrl(): void {
  if (urlCaptured) return;
  urlCaptured = true;
  if (typeof window === 'undefined' || !window.location) return;
  const code = extractCodeFromUrl(window.location.href);
  if (code) {
    console.log('[referral] captured code from launch URL:', code);
    rememberPendingCode(code);
  }
}

export function attachNativeDeepLinkHook(): void {
  if (listenerAttached) return;
  if (!Capacitor.isNativePlatform()) return;
  listenerAttached = true;
  void CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    // Only act on referral-shaped URLs. Other deep links (Apple SSO,
    // password reset, custom schemes) are handled by their own listeners.
    if (!/\/i\/[A-Z0-9]{4,12}\b|[?&]ref=[A-Z0-9]{4,12}\b/i.test(url)) return;
    const code = extractCodeFromUrl(url);
    if (code) {
      console.log('[referral] captured code from deep link:', code);
      rememberPendingCode(code);
    }
  });
}

/** Boot helper — calls both. Safe to invoke once per process. */
export function initReferralBootstrap(): void {
  captureFromCurrentUrl();
  attachNativeDeepLinkHook();
}
